import { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { Plus, Trash2, AlertCircle, Save, FileText, FileSignature } from 'lucide-react';
import Accordion from '../Accordion';
import RichTextEditor from '../RichTextEditor';
import { useStore } from '../../store/useStore';
import { getUserTemplate, saveTemplate, updateTemplate } from '../../services/firebase/database/templates';

export default function DocumentTemplateSection() {
  const { user } = useStore();
  const [showHeaderEditor, setShowHeaderEditor] = useState(false);
  const [showFooterEditor, setShowFooterEditor] = useState(false);
  const [headerContent, setHeaderContent] = useState('');
  const [footerContent, setFooterContent] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadTemplate();
    }
  }, [user]);

  const loadTemplate = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const template = await getUserTemplate(user.id);
      if (template) {
        setTemplateId(template.id);
        setHeaderContent(template.header);
        setFooterContent(template.footer);
        setShowHeaderEditor(!!template.header);
        setShowFooterEditor(!!template.footer);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      const templateData = {
        header: headerContent,
        footer: footerContent,
        userId: user.id,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      if (templateId) {
        await updateTemplate(templateId, {
          header: headerContent,
          footer: footerContent,
          userId: user.id,
          updatedAt: Timestamp.fromDate(new Date())
        });
      } else {
        const id = await saveTemplate(templateData);
        setTemplateId(id);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveHeader = async () => {
    if (!templateId || !user) return;

    try {
      setIsLoading(true);
      setError(null);

      await updateTemplate(templateId, {
        header: ''
      });

      setHeaderContent('');
      setShowHeaderEditor(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFooter = async () => {
    if (!templateId || !user) return;

    try {
      setIsLoading(true);
      setError(null);

      await updateTemplate(templateId, {
        footer: ''
      });

      setFooterContent('');
      setShowFooterEditor(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Accordion title="Modelo de Documento">
      <div className="space-y-6">
        {error && (
          <div className="p-4 bg-error-500/10 border border-error-500/30 text-error-600 dark:text-error-400 rounded-xl flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Header Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-accent-500 dark:text-accent-400" />
              <h3 className="text-lg font-medium text-navy-900 dark:text-navy-100">Cabeçalho</h3>
            </div>
            {!showHeaderEditor ? (
              <button
                onClick={() => setShowHeaderEditor(true)}
                className="flex items-center space-x-2 text-accent-500 hover:text-accent-600 dark:text-accent-400 dark:hover:text-accent-300 transition-colors duration-200"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Cabeçalho</span>
              </button>
            ) : (
              <button
                onClick={handleRemoveHeader}
                className="flex items-center space-x-2 text-error-500 hover:text-error-600 dark:text-error-400 dark:hover:text-error-300 transition-colors duration-200"
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4" />
                <span>Remover Cabeçalho</span>
              </button>
            )}
          </div>

          {showHeaderEditor && (
            <div className="border border-navy-200 dark:border-navy-700 rounded-xl overflow-hidden">
              <RichTextEditor
                content={headerContent}
                onChange={setHeaderContent}
                id="header-editor"
              />
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FileSignature className="h-5 w-5 text-accent-500 dark:text-accent-400" />
              <h3 className="text-lg font-medium text-navy-900 dark:text-navy-100">Rodapé</h3>
            </div>
            {!showFooterEditor ? (
              <button
                onClick={() => setShowFooterEditor(true)}
                className="flex items-center space-x-2 text-accent-500 hover:text-accent-600 dark:text-accent-400 dark:hover:text-accent-300 transition-colors duration-200"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Rodapé</span>
              </button>
            ) : (
              <button
                onClick={handleRemoveFooter}
                className="flex items-center space-x-2 text-error-500 hover:text-error-600 dark:text-error-400 dark:hover:text-error-300 transition-colors duration-200"
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4" />
                <span>Remover Rodapé</span>
              </button>
            )}
          </div>

          {showFooterEditor && (
            <div className="border border-navy-200 dark:border-navy-700 rounded-xl overflow-hidden">
              <RichTextEditor
                content={footerContent}
                onChange={setFooterContent}
                id="footer-editor"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-navy-200 dark:border-navy-700">
          <button
            onClick={handleSaveTemplate}
            disabled={isLoading}
            className="inline-flex items-center px-5 py-2.5 btn-accent rounded-lg disabled:bg-navy-300 disabled:text-navy-500"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </Accordion>
  );
}
